import {
  inheritedAlt,
  resolveBrowserHref,
  resolveCuratedImage,
  type BrowserRef,
  type NewsItem,
} from 'volto-ministerio-publico-federal/components/View/CapaView/helpers';

/** Um card do carrossel, como o `ObjectListWidget` grava. */
export interface SubHomeCarrosselItem {
  /** Gerado pelo `ObjectListWidget`; serve de `key` estável na lista. */
  '@id'?: string;
  title?: string;
  href?: BrowserRef[];
  external?: boolean;
  /** Widget `image`: ver `resolveCuratedImage` sobre os formatos aceitos. */
  image?: string | BrowserRef[];
  alt?: string;
}

/** Dados do bloco `mpfSubHomeCarrossel`. */
export interface SubHomeCarrosselData {
  title?: string;
  subtitulo?: string;
  texto?: string;
  ctaLabel?: string;
  ctaHref?: BrowserRef[];
  ctaExternal?: boolean;
  items?: SubHomeCarrosselItem[];
}

/** View-model de um card. */
export interface SubHomeCarrosselCard {
  key: string;
  /** Posição em `items` — é o que o lápis passa ao editor. */
  sourceIndex: number;
  title: string;
  href?: string;
  external?: boolean;
  alt: string;
  item: SubHomeCarrosselItem;
}

/** Título padrão da faixa, quando o editor não escreve um. */
export const SUBHOME_CARROSSEL_TITLE = 'Carrossel destaques';

/** Escala da imagem do card. Mesma de `ONDE_ATUA_SCALE`. */
export const SUBHOME_CARROSSEL_SCALE = 'large';

/**
 * Teto de itens, como na anotação do Figma. O corte é no render, e não no
 * schema: o `ObjectListWidget` não sabe limitar a lista, então quem passar do
 * teto vê o aviso no editor e os excedentes simplesmente não entram na faixa.
 */
export const SUBHOME_CARROSSEL_MAX_ITEMS = 10;

/**
 * Quantos cards cabem na coluna do carrossel na maior largura.
 *
 * Fracionário de propósito: no Figma o último card é cortado pela borda do
 * cartão, sinalizando que a prateleira continua.
 *
 * DOIS cards inteiros mais um pedaço do terceiro, como na revisão do design.
 * A geometria crua do arquivo do Figma (coluna de 399px com cards de 277px)
 * daria 1.4 — um card só e um pedaço —, mas o que o layout comunica é a
 * prateleira com dois itens à mostra.
 */
export const SUBHOME_CARROSSEL_SLIDES = 2.3;

export const toSubHomeCarrosselCards = (
  items?: SubHomeCarrosselItem[],
): SubHomeCarrosselCard[] =>
  (items ?? []).slice(0, SUBHOME_CARROSSEL_MAX_ITEMS).map((item, index) => ({
    key: item['@id'] ?? `card-${index}`,
    sourceIndex: index,
    title: item.title ?? '',
    href: resolveBrowserHref(item.href),
    external: item.external,
    // Imagem própria → alt do editor; imagem herdada → texto alternativo do
    // conteúdo referenciado. Nunca o título.
    alt: item.alt || inheritedAlt(item.href) || '',
    item,
  }));

/**
 * Props de imagem do card, prontas para o `<Image>` do Volto.
 *
 * Primeiro a arte dedicada do campo Imagem; sem ela, a imagem do conteúdo
 * escolhido no link. Herança viva, e não cópia — ver `getOndeAtuaImageProps`.
 */
export const getSubHomeCarrosselImageProps = (
  card: SubHomeCarrosselCard,
  scale: string = SUBHOME_CARROSSEL_SCALE,
): { item: NewsItem } | { src: string } | null => {
  const picked = resolveCuratedImage(card.item.image, scale);
  if (picked) return picked;
  const ref = Array.isArray(card.item.href) ? card.item.href[0] : undefined;
  return ref?.image_scales ? { item: ref as unknown as NewsItem } : null;
};

/** A faixa só aparece no site quando tem texto ou pelo menos um card. */
export const subHomeCarrosselHasContent = (
  data?: SubHomeCarrosselData,
): boolean =>
  !!(
    data?.texto ||
    data?.subtitulo ||
    (Array.isArray(data?.items) && data.items.length > 0)
  );
